import { Carousel } from "antd";
import { useEffect, useRef, useState } from 'react';
// 工具函数：计算叉积
const crossProduct = (a: number[], b: number[], c: number[]) => {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
};

// Graham扫描算法
const grahamScan = (points: number[][]) => {
    if (points.length < 3) return points;

    let minYIndex = 0;
    for (let i = 1; i < points.length; i++) {
        if (
            points[i][1] < points[minYIndex][1] ||
            (points[i][1] === points[minYIndex][1] && points[i][0] < points[minYIndex][0])
        ) {
            minYIndex = i;
        }
    }

    const pivot = points[minYIndex];
    const remaining = points.filter((_, index) => index !== minYIndex);

    remaining.sort((a, b) => {
        const cross = crossProduct(pivot, a, b);
        if (cross === 0) {
            return (
                (a[0] - pivot[0]) ** 2 + (a[1] - pivot[1]) ** 2 -
                (b[0] - pivot[0]) ** 2 - (b[1] - pivot[1]) ** 2
            );
        }
        return cross > 0 ? -1 : 1;
    });

    const stack: number[][] = [pivot, remaining[0]];
    for (let i = 1; i < remaining.length; i++) {
        while (
            stack.length > 1 &&
            crossProduct(
                stack[stack.length - 2],
                stack[stack.length - 1],
                remaining[i]
            ) <= 0
        ) {
            stack.pop();
        }
        stack.push(remaining[i]);
    }
    return stack;
};

const TongueCheck = ({ visible, onCancel, record }: any) => {
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const value = [
        {
            index: 0,
            title: '舌色',
            value: record?.tongueColor?.type
        },
        {
            index: 1,
            title: '舌质',
            value: record?.tongueFurColour?.type
        },
        {
            index: 2,
            title: '舌形',
            value: record?.tongueShape
                ?.map((item: any) => item.type)
                ?.filter(Boolean)
                ?.join(', ')
        },
        ...(record?.relativeComparison?.length >= 2 ? [{
            index: 3,
            title: '舌象阴影',
            value: '如图所示'
        }] : [])
    ];

    // 绘制函数
    const drawOnCanvas = (
        canvas: HTMLCanvasElement,
        imageSrc: string,
        coordinates: number[][],
        fillColor: string = 'rgba(255, 0, 0, 0.5)',
        strokeColor: string = 'white',
        pointColor: string = 'white',
        background: boolean = false
    ) => {
        const ctx = canvas.getContext('2d');
        if (!ctx || !coordinates) {
            console.warn('绘制条件不满足');
            return;
        }
        const img = document.createElement('img');
        img.src = imageSrc;

        img.onload = () => {
            const displayWidth = canvas.clientWidth;
            const displayHeight = canvas.clientHeight;

            canvas.width = displayWidth;
            canvas.height = displayHeight;

            const scale = Math.min(
                displayWidth / img.width,
                displayHeight / img.height
            );
            const drawWidth = img.width * scale;
            const drawHeight = img.height * scale;
            // 背景处理逻辑
            if (background) {
                ctx.fillStyle = fillColor;
                ctx.fillRect(
                    (displayWidth - drawWidth) / 2,
                    (displayHeight - drawHeight) / 2,
                    drawWidth,
                    displayHeight
                );
            } else {
                ctx.drawImage(
                    img,
                    (displayWidth - drawWidth) / 2,
                    (displayHeight - drawHeight) / 2,
                    drawWidth,
                    drawHeight
                );
            }
            if (coordinates.length < 2) {
                return;
            }
            // 将原始图像中的坐标点转换为画布上的坐标点
            const transformCoordinate = (point: number[]) => {
                const scaleFactor = 1;
                return [
                    (point[0] / img.width) * drawWidth * scaleFactor +
                    (displayWidth - drawWidth * scaleFactor) / 2,
                    (point[1] / img.height) * drawHeight * scaleFactor +
                    (displayHeight - drawHeight * scaleFactor) / 2
                ];
            };

            const transformedPoints = grahamScan(coordinates).map(transformCoordinate);

            ctx.beginPath();
            ctx.moveTo(transformedPoints[0][0], transformedPoints[0][1]);
            transformedPoints.slice(1).forEach(p => ctx.lineTo(p[0], p[1]));
            ctx.closePath();

            if (fillColor !== 'transparent') {
                ctx.fillStyle = fillColor;
                ctx.fill();
            }

            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 2;
            ctx.lineJoin = 'round';
            ctx.stroke();

            // 关键点标记逻辑
            transformedPoints.forEach((p, i) => {
                ctx.beginPath();
                ctx.arc(p[0], p[1], 1, 0, Math.PI * 2);
                ctx.fillStyle = pointColor;
                ctx.fill();
            });
        };

        img.onerror = (e) => console.error('图片加载失败', e);
    };

    // 初始化绘制
    useEffect(() => {
        canvasRefs.current.forEach((canvas, index) => {
            if (!canvas) return;

            const configs = [
                {
                    data: record.relativeOriginalCoordinates,
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                },
                {
                    data: record.relativeComparison,
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                },
                {
                    data: [],
                    fill: 'transparent',
                    stroke: 'white',
                    point: 'white',
                },
                {
                    data: record.relativeComparison,
                    fill: '#673ab7',
                    stroke: 'yellow',
                    point: 'yellow',
                    background: true
                }
            ];

            if (index < configs.length) {
                drawOnCanvas(
                    canvas,
                    record?.crop_url,
                    configs[index].data,
                    configs[index].fill,
                    configs[index].stroke,
                    configs[index].point,
                    configs[index].background,
                );
            }
        });
    }, [record]);

    return visible ? (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.8)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}
            onClick={onCancel}
        >
            <div
                style={{
                    width: '80%',
                    maxWidth: 800,
                    border: '2px solid #58BEBF',
                    borderRadius: 8,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    padding: 16,
                    position: 'relative'
                }}
                onClick={(e) => { e.stopPropagation() }}
            >
                <Carousel
                    afterChange={current => setActiveIndex(current)}
                    dots={{ className: 'custom-dots' }}
                    effect="fade"
                    style={{
                        overflow: 'hidden'
                    }}
                >
                    {value.map((title, index) => (
                        <div key={index} style={{
                            background: index === 3 ? '#673ab7' : 'transparent',
                            minHeight: 400,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                width: '100%',
                                height: 255,
                                position: 'relative',
                                backgroundColor: '#f0f0f0'
                            }}>
                                <canvas
                                    ref={el => { canvasRefs.current[index] = el; }}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </Carousel>

            </div>
            <div style={{
                padding: 16,
                textAlign: 'center'
            }}>
                <p style={{
                    margin: 0,
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#58BEBF'
                }}>{value[activeIndex].title}</p>
                <p style={{
                    margin: 0,
                    color: 'white'
                }}>
                    {value[activeIndex].value}
                </p>
            </div>
        </div>
    ) : null;
};

export default TongueCheck;